import React from 'react';
import styled from 'styled-components';
import { FaCheck, FaTimes } from 'react-icons/fa';
import Button from './Button';

interface PriceFeature {
  text: string;
  included: boolean;
}

interface PriceCardProps {
  title: string;
  price: number;
  period?: string;
  description: string;
  features: PriceFeature[];
  isPopular?: boolean;
  buttonText?: string;
  buttonLink?: string;
}

const CardWrapper = styled.div<{ isPopular?: boolean }>`
  background-color: var(--white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--box-shadow);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  border: ${props => props.isPopular ? '2px solid var(--blue)' : '1px solid #eee'};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--box-shadow-lg);
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  right: 20px;
  background-color: var(--blue);
  color: var(--white);
  padding: 0.25rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--gray-dark);
`;

const CardPrice = styled.div`
  margin-bottom: 1.5rem;
`;

const PriceAmount = styled.span`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--blue);
`;

const PricePeriod = styled.span`
  font-size: 1rem;
  color: var(--gray);
  margin-left: 0.5rem;
`;

const CardDescription = styled.p`
  margin-bottom: 1.5rem;
  color: var(--gray);
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
  flex-grow: 1;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const FeatureIcon = styled.span<{ included: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 0.75rem;
  background-color: ${props => props.included ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)'};
  color: ${props => props.included ? 'var(--green)' : 'var(--red)'};
`;

const FeatureText = styled.span<{ included: boolean }>`
  color: ${props => props.included ? 'var(--gray-dark)' : 'var(--gray)'};
  text-decoration: ${props => props.included ? 'none' : 'line-through'};
`;

const PriceCard: React.FC<PriceCardProps> = ({
  title,
  price,
  period = 'pago único',
  description,
  features,
  isPopular = false,
  buttonText = 'Contratar ahora',
  buttonLink = '/contacto',
}) => {
  return (
    <CardWrapper isPopular={isPopular}>
      {isPopular && <PopularBadge>Más popular</PopularBadge>}
      
      <CardTitle>{title}</CardTitle>
      
      <CardPrice>
        <PriceAmount>{price}€</PriceAmount>
        <PricePeriod>{period}</PricePeriod>
      </CardPrice>
      
      <CardDescription>{description}</CardDescription>
      
      <FeaturesList>
        {features.map((feature, index) => (
          <FeatureItem key={index}>
            <FeatureIcon included={feature.included}>
              {feature.included ? <FaCheck /> : <FaTimes />}
            </FeatureIcon>
            <FeatureText included={feature.included}>{feature.text}</FeatureText>
          </FeatureItem>
        ))}
      </FeaturesList>
      
      <Button
        as="a"
        href={buttonLink}
        variant={isPopular ? 'primary' : 'outline'}
        fullWidth
      >
        {buttonText}
      </Button>
    </CardWrapper>
  );
};

export default PriceCard; 